import { z } from "zod";
import type { OrganizationTypeRef } from "./organization-types";
import type { OrganizationTypeCount } from "./responsible-organizations";
import { paginationQuery, searchParam } from "./pagination";

/**
 * `/main-functions` and `/sub-functions` — the accreditation category tree.
 *
 * Main function is the participant-type discriminator (Athlete, Media, VIP,
 * Workforce…): it decides the card's colour bar and the quota bucket. Sub
 * function is the specific role beneath it (Press Photographer under Media).
 * Door access is decided by zone access rules (`access_rules`, 0027) keyed on
 * the main function, optionally narrowed by sub function and national
 * federation. There is ONE sub-functions surface, filterable by main function;
 * the predecessor's duplicated pages are not coming back.
 *
 * `sort_order` exists because the card hierarchy (A, B, C … FA, FO1 … VIP) is
 * not alphabetical. Every list orders by (sort_order, code).
 */

// ── Main functions ──────────────────────────────────────────────────────────

export const MAIN_FUNCTION_SORT_KEYS = ["sort_order", "code", "name", "category", "created_at"] as const;
export type MainFunctionSortKey = (typeof MAIN_FUNCTION_SORT_KEYS)[number];

export const listMainFunctionsQuery = paginationQuery.extend({
	search: searchParam.optional(),
	category: z.string().trim().min(1).max(100).optional(),
	/** Categories of exactly this organisation type. */
	organization_type_id: z.string().uuid().optional(),
	sort: z.enum(MAIN_FUNCTION_SORT_KEYS).optional(),
	dir: z.enum(["asc", "desc"]).optional(),
});
export type ListMainFunctionsQuery = z.infer<typeof listMainFunctionsQuery>;

/**
 * `GET /main-functions/options?organization_type_id=` — the categories OFFERED
 * to that type: its own plus those with no type (offered to every type).
 */
export const mainFunctionOptionsQuery = z.object({
	organization_type_id: z.string().uuid().optional(),
});
export type MainFunctionOptionsQuery = z.infer<typeof mainFunctionOptionsQuery>;

/** One row of `GET /main-functions`. */
export interface MainFunctionListItem {
	main_function_id: string;
	main_function_code: string;
	main_function_name: string;
	/** Free-text grouping label for quota reports ("Contingent", "Sekretariat"). */
	main_function_category: string | null;
	/** Colour bar printed on every card of this function — `#rrggbb`. */
	card_color: string | null;
	/** Text colour on that banner — `#rrggbb`; null = white. */
	card_ink_color: string | null;
	/** The organisation type this category is offered to; null = every type. */
	organization_type: OrganizationTypeRef | null;
	sort_order: number;
	sub_functions_count: number;
	/** Cards issued under this function. A function in use cannot be deleted. */
	cards_count: number;
	/** User-group scope rows (`user_group_scopes`) naming this function — scoped groups' visibility. */
	scopes_count: number;
	created_at: string;
	updated_at: string;
}

/** `GET /main-functions/options` — the whole list, for `<select>` elements. */
export interface MainFunctionOption {
	main_function_id: string;
	main_function_code: string;
	main_function_name: string;
	card_color: string | null;
	card_ink_color: string | null;
	organization_type: OrganizationTypeRef | null;
}

/** `GET /main-functions/stats` — one statement, unfiltered. */
export interface MainFunctionStats {
	total: number;
	/** Sub-functions under every main function. */
	sub_functions_total: number;
	/** Every organisation type (zero counts included), then the no-type bucket — "offered to every type". */
	by_organization_type: OrganizationTypeCount[];
}

// ── Sub functions ───────────────────────────────────────────────────────────

export const SUB_FUNCTION_SORT_KEYS = ["code", "name", "main_function", "created_at"] as const;
export type SubFunctionSortKey = (typeof SUB_FUNCTION_SORT_KEYS)[number];

export const listSubFunctionsQuery = paginationQuery.extend({
	search: searchParam.optional(),
	main_function_id: z.string().uuid().optional(),
	sort: z.enum(SUB_FUNCTION_SORT_KEYS).optional(),
	dir: z.enum(["asc", "desc"]).optional(),
});
export type ListSubFunctionsQuery = z.infer<typeof listSubFunctionsQuery>;

/** One row of `GET /sub-functions`. */
export interface SubFunctionListItem {
	sub_function_id: string;
	main_function_id: string;
	main_function_code: string;
	main_function_name: string;
	sub_function_code: string;
	sub_function_name: string;
	/** Whether a card in this role must name a sport before it can be approved. */
	requires_sport: boolean;
	/** Cards issued in this role. A role in use cannot be deleted. */
	cards_count: number;
	/** User-group scope rows (`user_group_scopes`) naming this role — scoped groups' visibility. */
	scopes_count: number;
	created_at: string;
	updated_at: string;
}

/** `GET /sub-functions/options` — for the dependent dropdown, filtered client-side by `main_function_id`. */
export interface SubFunctionOption {
	sub_function_id: string;
	main_function_id: string;
	sub_function_code: string;
	sub_function_name: string;
	requires_sport: boolean;
}

// ── Write requests ──────────────────────────────────────────────────────────

export interface FunctionFormMessages {
	nameRequired: string;
	codeRequired: string;
	/** Letters, digits, `-` and `_` only — codes like E-P, FO1, W_PETUGAS. */
	codeFormat: string;
	/** `#rrggbb`. */
	cardColorFormat: string;
	sortOrderInvalid: string;
}

export const enFunctionFormMessages: FunctionFormMessages = {
	nameRequired: "Enter a name",
	codeRequired: "Enter a code",
	codeFormat: "Letters, numbers, - and _ only",
	cardColorFormat: "Like #D4AF37",
	sortOrderInvalid: "Enter a whole number, 0 or more",
};

/** Letters (either case), digits, `-` and `_`; at most 20. Checked AFTER trimming (bug 5). */
const FUNCTION_CODE_PATTERN = /^[A-Za-z0-9_-]+$/;

/**
 * SUB-function codes like E-P, FO1, W_PETUGAS — trimmed, checked, then stored
 * uppercase so lookups never case-fold. Shared by the web forms and the API
 * validators.
 */
export const makeFunctionCodeField = (m: FunctionFormMessages = enFunctionFormMessages) =>
	z
		.string()
		.trim()
		.min(1, m.codeRequired)
		.max(20)
		.regex(FUNCTION_CODE_PATTERN, m.codeFormat)
		.transform((v) => v.toUpperCase());
export const functionCodeField = makeFunctionCodeField();

/**
 * MAIN-function (category) codes — Aa, Ao, TO, EP — trimmed and checked, then
 * kept in the case the admin typed: the case prints on the card and is
 * meaningful (Aa Athlete vs Ao Team Official). Uniqueness is case-insensitive
 * in the database (`uq_main_functions_code` on lower(code), 0018), so "Aa"
 * and "AA" cannot both exist.
 */
export const makeMainFunctionCodeField = (m: FunctionFormMessages = enFunctionFormMessages) =>
	z.string().trim().min(1, m.codeRequired).max(20).regex(FUNCTION_CODE_PATTERN, m.codeFormat);
export const mainFunctionCodeField = makeMainFunctionCodeField();

/** Optional inputs bind as `""`; the `to*Body` helpers drop or null them. */
const optionalCardColor = (m: FunctionFormMessages) =>
	z
		.string()
		.trim()
		.regex(/^#[0-9a-fA-F]{6}$/, m.cardColorFormat)
		.transform((v) => v.toUpperCase())
		.or(z.literal(""));

const mainFunctionFields = (m: FunctionFormMessages) => ({
	main_function_code: makeMainFunctionCodeField(m),
	main_function_name: z.string().trim().min(1, m.nameRequired).max(200),
	main_function_category: z.string().trim().max(100),
	card_color: optionalCardColor(m),
	/**
	 * Optional in the FORM so a form built before these fields existed still
	 * type-checks; `""` / absent = none.
	 */
	card_ink_color: optionalCardColor(m).optional(),
	organization_type_id: z.string().uuid().or(z.literal("")).optional(),
	sort_order: z.coerce.number({ invalid_type_error: m.sortOrderInvalid }).int(m.sortOrderInvalid).min(0, m.sortOrderInvalid).max(9999, m.sortOrderInvalid),
});

export function makeMainFunctionForm(m: FunctionFormMessages = enFunctionFormMessages) {
	return z.object(mainFunctionFields(m));
}
export const mainFunctionForm = makeMainFunctionForm();
export type MainFunctionForm = z.infer<typeof mainFunctionForm>;

const subFunctionFields = (m: FunctionFormMessages) => ({
	sub_function_code: makeFunctionCodeField(m),
	sub_function_name: z.string().trim().min(1, m.nameRequired).max(200),
	requires_sport: z.boolean(),
});

export function makeSubFunctionForm(m: FunctionFormMessages = enFunctionFormMessages) {
	return z.object(subFunctionFields(m));
}
export const subFunctionForm = makeSubFunctionForm();
export type SubFunctionForm = z.infer<typeof subFunctionForm>;

export interface CreateMainFunctionBody {
	main_function_code: string;
	main_function_name: string;
	main_function_category?: string;
	card_color?: string;
	card_ink_color?: string;
	organization_type_id?: string;
	sort_order?: number;
}

/** `null` clears a field; omitted leaves it alone. */
export interface UpdateMainFunctionBody {
	main_function_code?: string;
	main_function_name?: string;
	main_function_category?: string | null;
	card_color?: string | null;
	card_ink_color?: string | null;
	organization_type_id?: string | null;
	sort_order?: number;
	/** Headline for this change in the Activity feed. */
	reason?: string;
}

/** `main_function_id` rides in the URL/query, not the form. */
export interface CreateSubFunctionBody {
	main_function_id: string;
	sub_function_code: string;
	sub_function_name: string;
	requires_sport?: boolean;
}

export interface UpdateSubFunctionBody {
	sub_function_code?: string;
	sub_function_name?: string;
	requires_sport?: boolean;
	/** Headline for this change in the Activity feed. */
	reason?: string;
}

export const toCreateMainFunctionBody = (f: MainFunctionForm): CreateMainFunctionBody => ({
	main_function_code: f.main_function_code,
	main_function_name: f.main_function_name,
	...(f.main_function_category ? { main_function_category: f.main_function_category } : {}),
	...(f.card_color ? { card_color: f.card_color } : {}),
	...(f.card_ink_color ? { card_ink_color: f.card_ink_color } : {}),
	...(f.organization_type_id ? { organization_type_id: f.organization_type_id } : {}),
	sort_order: f.sort_order,
});

/** Only what actually changed, so the audit row's `changed_fields` names the edit. */
export function toUpdateMainFunctionBody(f: MainFunctionForm, before: MainFunctionListItem): UpdateMainFunctionBody {
	const body: UpdateMainFunctionBody = {};
	if (f.main_function_code !== before.main_function_code) body.main_function_code = f.main_function_code;
	if (f.main_function_name !== before.main_function_name) body.main_function_name = f.main_function_name;
	if (f.sort_order !== before.sort_order) body.sort_order = f.sort_order;
	const diffNullable = (next: string, prev: string | null) => (next === "" ? (prev === null ? undefined : null) : next === prev ? undefined : next);
	const category = diffNullable(f.main_function_category, before.main_function_category);
	if (category !== undefined) body.main_function_category = category;
	const color = diffNullable(f.card_color, before.card_color);
	if (color !== undefined) body.card_color = color;
	// Absent from the form = not on this form, so never sent as a change.
	if (f.card_ink_color !== undefined) {
		const ink = diffNullable(f.card_ink_color, before.card_ink_color);
		if (ink !== undefined) body.card_ink_color = ink;
	}
	if (f.organization_type_id !== undefined) {
		const type = diffNullable(f.organization_type_id, before.organization_type?.id ?? null);
		if (type !== undefined) body.organization_type_id = type;
	}
	return body;
}

export const toCreateSubFunctionBody = (f: SubFunctionForm, mainFunctionId: string): CreateSubFunctionBody => ({
	main_function_id: mainFunctionId,
	sub_function_code: f.sub_function_code,
	sub_function_name: f.sub_function_name,
	requires_sport: f.requires_sport,
});

export function toUpdateSubFunctionBody(f: SubFunctionForm, before: SubFunctionListItem): UpdateSubFunctionBody {
	const body: UpdateSubFunctionBody = {};
	if (f.sub_function_code !== before.sub_function_code) body.sub_function_code = f.sub_function_code;
	if (f.sub_function_name !== before.sub_function_name) body.sub_function_name = f.sub_function_name;
	if (f.requires_sport !== before.requires_sport) body.requires_sport = f.requires_sport;
	return body;
}
